import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import FormField from '@/components/atoms/FormField';
import StyledSingleDatePicker from '@/components/atoms/StyledSingleDatePicker/StyledSingleDatePicker';
import TextField from '@/components/atoms/TextField';

import type { AuditTypingStep } from '../../useAuditLogFilterState';

import styles from './DateTimeFilter.module.scss';

// As for the typed filters: a pause this long reports the time.
const DEBOUNCE_MS = 300;

type DateTimeStep = 'pick' | AuditTypingStep;

interface DateTimeFilterProps {
    label: string;
    value: Date | undefined;
    /** Undefined removes the bound. */
    onChange: (next: Date | undefined, step: DateTimeStep) => void;
    /** Read with the time input and shown on hover, so the row keeps one line. */
    hint?: string;
}

function twoDigits(value: number): string {
    return String(value).padStart(2, '0');
}

function timeOf(date: Date): string {
    return `${twoDigits(date.getHours())}:${twoDigits(date.getMinutes())}`;
}

function atTime(day: Date, time: string): Date {
    const [hours, minutes] = time.split(':').map(Number);
    const next = new Date(day);
    next.setHours(Number.isFinite(hours) ? hours : 0, Number.isFinite(minutes) ? minutes : 0, 0, 0);
    return next;
}

/**
 * A bound to the minute, in the browser's time zone: a day, then a time of day on it. The
 * time waits for a day, since a time alone is no instant. Each hour or minute typed would
 * otherwise rewrite the URL and refetch, so the time reports once the typing pauses, and
 * at once on blur.
 */
export default function DateTimeFilter({ label, value, onChange, hint }: DateTimeFilterProps) {
    const { t } = useTranslation();
    const id = useId();
    // Its own id, so the time input does not take the field's, which names the day.
    const timeId = useId();
    const applied = value === undefined ? '' : timeOf(value);
    // A browser reports a time only once both parts are complete, and '' until then.
    const [draft, setDraft] = useState(applied);
    const instant = value?.getTime();
    // The bound this field expects after its own reports, to tell them from a change made
    // elsewhere: a picked day, a chip, "Clear all" or Back. The whole instant, not just the
    // time of day, since Back can restore another day at the same hour and minute.
    const expected = useRef(instant);
    const latest = useRef({ value, onChange });
    const timer = useRef<number | undefined>(undefined);
    // Only a time the user typed is committed on blur: the input holds minutes, so
    // committing an untouched bound would drop the seconds a URL can carry.
    const edited = useRef(false);

    useEffect(() => {
        latest.current = { value, onChange };
    }, [value, onChange]);

    useEffect(() => {
        if (instant === expected.current) return;
        // A bound set elsewhere wins over a time still being typed here.
        expected.current = instant;
        window.clearTimeout(timer.current);
        edited.current = false;
        setDraft(applied);
    }, [instant, applied]);

    useEffect(() => {
        if (draft === '' || draft === applied) return;
        timer.current = window.setTimeout(() => {
            const bound = latest.current.value;
            if (bound === undefined) return;
            const next = atTime(bound, draft);
            expected.current = next.getTime();
            latest.current.onChange(next, 'type');
        }, DEBOUNCE_MS);
        return () => window.clearTimeout(timer.current);
    }, [draft, applied]);

    function handleDate(day: Date | null) {
        onChange(day === null ? undefined : atTime(day, applied || '00:00'), 'pick');
    }

    function handleBlur() {
        window.clearTimeout(timer.current);
        if (value === undefined || !edited.current) return;
        edited.current = false;
        // A half-typed time is dropped for the one the bound has.
        const time = draft === '' ? applied : draft;
        if (draft === '') setDraft(applied);
        const next = atTime(value, time);
        expected.current = next.getTime();
        onChange(next, 'commit');
    }

    return (
        <FormField layout="inline" htmlFor={id} label={label} hint={hint} hintTone="secondary">
            <div className={styles.row} title={hint}>
                <div className={styles.date}>
                    <StyledSingleDatePicker
                        id={id}
                        date={value ?? null}
                        onDateChange={handleDate}
                    />
                </div>
                <TextField
                    id={timeId}
                    type="time"
                    className={styles.time}
                    aria-label={t('auditLog.filters.timeOf', { label })}
                    disabled={value === undefined}
                    value={value === undefined ? '' : draft}
                    onChange={(event) => {
                        edited.current = true;
                        setDraft(event.target.value);
                    }}
                    onBlur={handleBlur}
                />
            </div>
        </FormField>
    );
}
